import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, ComposedChart, Line, Area } from "recharts";
import {
  PARAM_BOUNDS, DEFAULT_CONSTRAINT_CONFIG,
  HISTORICAL_WORST_BY_WINDOW, VALID_WINDOWS,
  analyze, backtest, resolveWindow,
} from '../lib/yield-engine';
import COLORS from '../lib/colors.js';

const fmt = (v, d = 2) => (v * 100).toFixed(d) + "%";
const fmtN = (v, d = 2) => v.toFixed(d);

const DEFAULT_ETH_DROP_PCT = DEFAULT_CONSTRAINT_CONFIG.ethDropPct;
const DEFAULT_DEPEG_PCT = DEFAULT_CONSTRAINT_CONFIG.depegPct;

const Input = ({ label, value, onChange, suffix = "%", step = 0.1, min = 0, max = 100, hint }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
      <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>{label}</label>
      <span style={{ fontSize: 13, fontFamily: "'Fragment Mono', monospace", color: COLORS.textBody }}>{value}{suffix}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      style={{ width: "100%" }}
    />
    {hint && <div style={{ fontSize: 10, color: COLORS.textTertiary, marginTop: 2 }}>{hint}</div>}
  </div>
);

const LTVInput = ({ label, value, onChange, min = 0, max = 100, step = 1, safeMax, liqThreshold, hint }) => {
  const safeMaxPct = safeMax != null ? ((safeMax - min) / (max - min)) * 100 : null;
  const liqPct = liqThreshold != null ? ((liqThreshold - min) / (max - min)) * 100 : null;
  const inDanger = safeMax != null && value > safeMax;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>{label}</label>
        <span style={{ fontSize: 13, fontFamily: "'Fragment Mono', monospace", color: inDanger ? COLORS.negative : COLORS.textBody }}>{value}%</span>
      </div>
      <div style={{ position: "relative", height: 14 }}>
        {/* Safe range background bar */}
        <div style={{
          position: "absolute", top: 4, left: 0, right: 0, height: 6, borderRadius: 3,
          background: COLORS.negativeBg,
        }} />
        {safeMaxPct != null && (
          <div style={{
            position: "absolute", top: 4, left: 0, height: 6, borderRadius: 3,
            width: `${Math.min(100, Math.max(0, safeMaxPct))}%`,
            background: `linear-gradient(90deg, ${COLORS.positiveBg} 0%, ${COLORS.positiveBorder} 80%, ${COLORS.cautionBg} 100%)`,
          }} />
        )}
        {liqPct != null && (
          <div style={{
            position: "absolute", top: 1, height: 12,
            left: `${Math.min(100, Math.max(0, liqPct))}%`,
            width: 1, background: COLORS.negative + "40",
          }} />
        )}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value))}
          className={inDanger ? "danger" : ""}
          style={{ width: "100%", position: "relative", zIndex: 1 }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        {hint && <div style={{ fontSize: 10, color: COLORS.textTertiary }}>{hint}</div>}
        {safeMax != null && (
          <div style={{ fontSize: 9, fontFamily: "'Fragment Mono', monospace", color: inDanger ? COLORS.negative : COLORS.positive }}>
            safe ≤ {safeMax.toFixed(1)}%
          </div>
        )}
      </div>
    </div>
  );
};

const Toggle = ({ label, value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 12, userSelect: "none" }}
  >
    <div style={{
      width: 36, height: 20, borderRadius: 10,
      background: value ? COLORS.accent : COLORS.borderDark,
      border: `1px solid ${value ? COLORS.accent : COLORS.borderDark}`,
      position: "relative", transition: "all 0.2s"
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: value ? COLORS.bg : COLORS.textTertiary,
        position: "absolute", top: 1,
        left: value ? 18 : 1,
        transition: "all 0.2s"
      }} />
    </div>
    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>{label}</span>
  </div>
);

const Card = ({ title, color, children }) => (
  <div style={{
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: "20px 24px",
    borderTop: `3px solid ${color}`,
  }}>
    <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color, margin: "0 0 16px", fontFamily: "'Shippori Mincho B1', serif" }}>{title}</h3>
    {children}
  </div>
);

const Metric = ({ label, value, color = COLORS.ink, sub }) => (
  <div style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.borderLight}` }}>
    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.textTertiary, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'Fragment Mono', monospace", color }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: COLORS.textTertiary, marginTop: 2 }}>{sub}</div>}
  </div>
);

const StatusBadge = ({ level }) => {
  const colors = {
    safe: { bg: COLORS.positiveBg, border: COLORS.positiveBorder, text: COLORS.positive, label: "SAFE" },
    caution: { bg: COLORS.cautionBg, border: COLORS.cautionBorder, text: COLORS.caution, label: "CAUTION" },
    danger: { bg: COLORS.negativeBg, border: COLORS.negativeBorder, text: COLORS.negative, label: "DANGER" },
  };
  const c = colors[level] || colors.safe;
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
      padding: "3px 8px", borderRadius: 4,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text
    }}>{c.label}</span>
  );
};

export default function Simulator() {
  // Dawn Senior Vault
  const [srYield, setSrYield] = useState(7);

  // royETH (Aave)
  const [stakingYield, setStakingYield] = useState(2.7);
  const [aaveSupplyYield, setAaveSupplyYield] = useState(0.04);
  const [aaveLTV, setAaveLTV] = useState(39);
  const [usdcBorrowRate, setUsdcBorrowRate] = useState(5.6);
  const [aaveLiqThreshold, setAaveLiqThreshold] = useState(83);

  // eigenETH / Morpho Loop
  const [morphoLTV, setMorphoLTV] = useState(66);
  const [ethBorrowPremium, setEthBorrowPremium] = useState(0.5);
  const [morphoLiqThreshold, setMorphoLiqThreshold] = useState(85);
  const [modelDepeg, setModelDepeg] = useState(true);
  const [depegPct, setDepegPct] = useState(10);

  // Vanilla wstETH/ETH Loop Comparison
  const [baseEthBorrowRate, setBaseEthBorrowRate] = useState(2.2);
  const [vanillaSupplyYield, setVanillaSupplyYield] = useState(0.02);
  const [mirrorAaveSupply, setMirrorAaveSupply] = useState(true);

  const effectiveVanillaSupply = mirrorAaveSupply ? aaveSupplyYield : vanillaSupplyYield;
  const eigenEthBorrowRate = baseEthBorrowRate + ethBorrowPremium;

  // Constraint config
  const [ethDropPct, setEthDropPct] = useState(DEFAULT_ETH_DROP_PCT);
  const [drawdownWindow, setDrawdownWindow] = useState(0);
  const [showConstraints, setShowConstraints] = useState(false);
  const [showBacktestDetail, setShowBacktestDetail] = useState(null);

  // Config export/import
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [importText, setImportText] = useState("");

  const getConfig = () => ({
    srYield, stakingYield, aaveSupplyYield, aaveLTV, usdcBorrowRate, aaveLiqThreshold,
    morphoLTV, ethBorrowPremium, morphoLiqThreshold, modelDepeg, depegPct,
    baseEthBorrowRate, vanillaSupplyYield, mirrorAaveSupply, ethDropPct, drawdownWindow,
  });

  const configJson = JSON.stringify(getConfig(), null, 2);

  const handleCopy = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = configJson;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    } catch {}
    setShowExport(true);
    setShowImport(false);
  };

  const handleImport = () => {
    try {
      const cfg = JSON.parse(importText);
      if (cfg.srYield != null) setSrYield(cfg.srYield);
      if (cfg.stakingYield != null) setStakingYield(cfg.stakingYield);
      if (cfg.aaveSupplyYield != null) setAaveSupplyYield(cfg.aaveSupplyYield);
      if (cfg.aaveLTV != null) setAaveLTV(cfg.aaveLTV);
      if (cfg.usdcBorrowRate != null) setUsdcBorrowRate(cfg.usdcBorrowRate);
      if (cfg.aaveLiqThreshold != null) setAaveLiqThreshold(cfg.aaveLiqThreshold);
      if (cfg.morphoLTV != null) setMorphoLTV(cfg.morphoLTV);
      if (cfg.ethBorrowPremium != null) setEthBorrowPremium(cfg.ethBorrowPremium);
      if (cfg.morphoLiqThreshold != null) setMorphoLiqThreshold(cfg.morphoLiqThreshold);
      if (cfg.modelDepeg != null) setModelDepeg(cfg.modelDepeg);
      if (cfg.depegPct != null) setDepegPct(cfg.depegPct);
      if (cfg.baseEthBorrowRate != null) setBaseEthBorrowRate(cfg.baseEthBorrowRate);
      if (cfg.vanillaSupplyYield != null) setVanillaSupplyYield(cfg.vanillaSupplyYield);
      if (cfg.mirrorAaveSupply != null) setMirrorAaveSupply(cfg.mirrorAaveSupply);
      if (cfg.ethDropPct != null) setEthDropPct(cfg.ethDropPct);
      if (cfg.drawdownWindow != null) setDrawdownWindow(cfg.drawdownWindow);
      setShowImport(false);
      setImportText("");
    } catch {
      alert("Invalid JSON config");
    }
  };

  const calc = useMemo(() => {
    const config = {
      srYield, stakingYield, aaveSupplyYield, aaveLTV, usdcBorrowRate,
      aaveLiqThreshold, morphoLTV, ethBorrowPremium, morphoLiqThreshold,
      modelDepeg, depegPct, baseEthBorrowRate, vanillaSupplyYield,
      mirrorAaveSupply, drawdownWindow,
    };
    const constraintConfig = {
      ethDropPct,
      depegPct: modelDepeg ? depegPct : DEFAULT_DEPEG_PCT
    };
    const r = analyze(config, constraintConfig);
    const bt = backtest(config);

    // Compute max survivable window (not directly in engine output)
    let maxSurvivableWindow = 0;
    for (const wr of bt.windowResults) {
      if (wr.allPass) maxSurvivableWindow = wr.window;
    }

    // Compute vanilla at 90% LTV (not in engine's analyze output)
    const sk = stakingYield / 100;
    const vsup = effectiveVanillaSupply / 100;
    const bebr = baseEthBorrowRate / 100;
    const vanillaLeverage90 = 10;
    const vanillaYield90 = vanillaLeverage90 * (sk + vsup) - (vanillaLeverage90 - 1) * bebr;
    const vanillaGross90 = vanillaLeverage90 * (sk + vsup);
    const vanillaBorrowCost90 = (vanillaLeverage90 - 1) * bebr;

    // Resolve drawdown window info
    let effectiveEthDrop = ethDropPct;
    let resolvedWin = null;
    let windowHist = null;
    if (drawdownWindow > 0) {
      resolvedWin = resolveWindow(drawdownWindow);
      windowHist = HISTORICAL_WORST_BY_WINDOW[resolvedWin];
      if (windowHist) {
        effectiveEthDrop = windowHist.maxDrop;
      }
    }

    // Add UI-friendly labels to constraints (engine uses 'description')
    const constraintLabels = {
      royETHPositive: "royETH yield positive",
      morphoSpreadPositive: "Loop spread positive",
      vanillaSpreadPositive: "Vanilla loop spread positive",
      eigenBeatVanilla: "eigenETH > vanilla loop",
      eigenBeat1x: "eigenETH > 1x wstETH",
      aaveSurviveDrop: drawdownWindow > 0 && windowHist
        ? `Survives ${resolvedWin}d worst (${effectiveEthDrop.toFixed(1)}%, ${windowHist.date})`
        : `Survives ${ethDropPct}% ETH drop`,
      morphoSurviveDepeg: `Survives ${(modelDepeg ? depegPct : DEFAULT_DEPEG_PCT)}% depeg`,
    };
    const uiConstraints = {};
    for (const [key, c] of Object.entries(r.constraints)) {
      uiConstraints[key] = { ...c, label: constraintLabels[key] || c.description };
    }

    // Add UI-friendly labels to thresholds (engine uses 'description')
    const thresholdLabels = {
      maxAaveLTV: drawdownWindow > 0 && windowHist
        ? `Max LTV for ${resolvedWin}d worst (${effectiveEthDrop.toFixed(1)}%)`
        : `Max LTV for ${ethDropPct}% ETH drop`,
      maxMorphoLTV: `Max Loop LTV for ${(modelDepeg ? depegPct : DEFAULT_DEPEG_PCT)}% depeg`,
      maxBorrowPremium: "Max borrow premium (eigenETH > vanilla)",
      breakEvenSrYield: "Min srRoyUSDC yield (royETH > 0)",
      breakEvenBaseRate: "Max base ETH rate (morpho spread > 0)",
    };
    const uiThresholds = {};
    for (const [key, t] of Object.entries(r.thresholds)) {
      uiThresholds[key] = { ...t, label: thresholdLabels[key] || t.description };
    }

    // Map engine output to the shape the UI expects (all values in decimals)
    return {
      dawnYield: r.yields.dawn,
      royETHYield: r.yields.royETH,
      eigenvaultYield: r.yields.eigenETH,
      roycoStakingComponent: r.yields.royETHComponents.staking,
      roycoSupplyComponent: r.yields.royETHComponents.supply,
      roycoSpreadComponent: r.yields.royETHComponents.spread,
      usdcSpread: (srYield / 100) - (usdcBorrowRate / 100),
      leverage: r.leverage,
      eigenRoyETHGross: r.yields.eigenETHComponents.gross,
      eigenBorrowCost: r.yields.eigenETHComponents.borrowCost,
      aaveMaxDrop: r.liquidation.aave.maxDrop,
      aaveHealthFactor: r.liquidation.aave.healthFactor,
      aaveDrawdowns: r.liquidation.aave.drawdowns.map(d => ({ ...d, label: `-${d.drop}%` })),
      morphoMaxDepeg: r.liquidation.morpho.maxDepeg,
      morphoHF: r.liquidation.morpho.healthFactor,
      morphoLTVAfterDepeg: r.liquidation.morpho.ltvAfterDepeg,
      morphoHFAfterDepeg: r.liquidation.morpho.hfAfterDepeg,
      morphoDepegScenarios: r.liquidation.morpho.depegScenarios.map(d => ({ ...d, label: `-${d.depeg}%` })),
      vanillaYield: r.yields.vanilla,
      vanillaEffBorrowRate: bebr,
      vanillaGross: r.yields.vanillaComponents.gross,
      vanillaBorrowCost: r.yields.vanillaComponents.borrowCost,
      yieldAdvantage: r.comparisons.vsVanilla,
      vanillaLeverage90,
      vanillaYield90,
      vanillaGross90,
      vanillaBorrowCost90,
      yieldVs90: r.yields.eigenETH - vanillaYield90,
      oneXwstETH: r.yields.oneXwstETH,
      yieldVs1x: r.comparisons.vs1x,
      vanillaSpread: r.spreads.vanillaLoop,
      morphoSpread: r.spreads.morphoLoop,
      constraints: uiConstraints,
      passingCount: r.constraintsSummary.passingCount,
      totalCount: r.constraintsSummary.totalCount,
      thresholds: uiThresholds,
      safeLTV: r.safeLTV,
      // Map backtest results to UI shape
      backtestByWindow: VALID_WINDOWS.map((w) => {
        const wr = bt.windowResults.find(wr => wr.window === w);
        const events = bt.eventResults.filter(e => e.window === w).map(e => ({
          ...e,
          aaveSurvives: e.aaveSurvives,
          headroom: e.aaveHeadroom !== undefined ? e.aaveHeadroom : 0,
        }));
        return {
          window: w,
          totalEvents: events.length,
          allPass: wr ? wr.allPass : true,
          passCount: wr ? wr.passCount : events.length,
          failCount: wr ? wr.failCount : 0,
          worstDrop: wr ? wr.worstDrop : 0,
          worstDate: wr ? wr.worstDate : '',
          worstEvent: wr ? wr.worstEventName : '',
          events,
        };
      }),
      maxSurvivableWindow,
      morphoSurvives: bt.morphoSurvives,
      effectiveEthDrop,
      resolvedWin,
      windowHist,
    };
  }, [srYield, stakingYield, aaveSupplyYield, aaveLTV, usdcBorrowRate,
      aaveLiqThreshold, morphoLTV, morphoLiqThreshold,
      modelDepeg, depegPct, baseEthBorrowRate, effectiveVanillaSupply,
      ethDropPct, drawdownWindow, ethBorrowPremium, mirrorAaveSupply, vanillaSupplyYield]);

  const yieldWaterfallData = [
    { name: "Dawn Senior\nsrRoyUSDC", yield: calc.dawnYield * 100, color: COLORS.positive },
    { name: "royETH", yield: calc.royETHYield * 100, color: COLORS.accent },
    { name: "eigenETH\nLooped", yield: calc.eigenvaultYield * 100, color: calc.eigenvaultYield >= 0 ? COLORS.accentAlt : COLORS.negative },
    { name: `Vanilla\n${fmtN(calc.leverage, 1)}x`, yield: calc.vanillaYield * 100, color: COLORS.ink },
    { name: "Vanilla\n90% LTV", yield: calc.vanillaYield90 * 100, color: COLORS.textTertiary },
  ];

  const roycoBreakdownData = [
    { name: "wstETH\nStaking", value: calc.roycoStakingComponent * 100, color: COLORS.positive },
    { name: "wstETH\nSupply", value: calc.roycoSupplyComponent * 100, color: COLORS.champagne },
    { name: "USDC\nSpread × LTV", value: calc.roycoSpreadComponent * 100, color: calc.roycoSpreadComponent >= 0 ? COLORS.accent : COLORS.negative },
  ];

  const riskLevel = (hf) => hf > 1.5 ? "safe" : hf > 1.15 ? "caution" : "danger";

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: COLORS.bg,
      color: COLORS.ink,
      minHeight: "100vh",
      padding: "24px 20px",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Shippori+Mincho+B1:wght@400;500;600;700;800&family=Fragment+Mono:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
  input[type=range] {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    height: 14px;
    cursor: pointer;
  }
  input[type=range]::-webkit-slider-runnable-track {
    background: ${COLORS.borderDark};
    height: 4px;
    border-radius: 2px;
  }
  input[type=range]::-moz-range-track {
    background: ${COLORS.borderDark};
    height: 4px;
    border-radius: 2px;
  }
  input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${COLORS.accent};
    border: 2px solid ${COLORS.bg};
    box-shadow: 0 0 0 1px ${COLORS.accent};
    margin-top: -5px;
    cursor: pointer;
  }
  input[type=range]::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${COLORS.accent};
    border: 2px solid ${COLORS.bg};
    box-shadow: 0 0 0 1px ${COLORS.accent};
    cursor: pointer;
  }
  input[type=range].danger::-webkit-slider-thumb {
    background: ${COLORS.negative};
    box-shadow: 0 0 0 1px ${COLORS.negative};
  }
  input[type=range].danger::-moz-range-thumb {
    background: ${COLORS.negative};
    box-shadow: 0 0 0 1px ${COLORS.negative};
  }
`}</style>

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ position: "relative", marginBottom: 24 }}>
          <div style={{ position: "absolute", top: 0, right: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.25em", textTransform: "uppercase", color: COLORS.textSecondary, fontFamily: "'Shippori Mincho B1', serif" }}>
            ROYCO
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 700, margin: "0 0 12px", letterSpacing: "-0.02em", lineHeight: 1.15, fontFamily: "'Shippori Mincho B1', serif", color: COLORS.ink }}>
            Yield & Liquidation Analyzer
          </h1>
          <div style={{ width: 40, height: 2, background: COLORS.accent, marginBottom: 12 }} />
          <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: "0 0 4px", fontFamily: "'Shippori Mincho B1', serif" }}>
            royETH Carry Trade → eigenETH Levered Loop
          </p>
          <p style={{ fontSize: 12, color: COLORS.textTertiary, margin: 0 }}>
            Interactive risk modeling
          </p>
          <div style={{ position: "absolute", bottom: 0, right: 0, display: "flex", gap: 8 }}>
          <button
            onClick={handleCopy}
            style={{
              background: showExport ? COLORS.accent : COLORS.surface,
              border: `1px solid ${showExport ? COLORS.accent : COLORS.border}`,
              borderRadius: 8,
              color: showExport ? COLORS.bg : COLORS.textSecondary,
              fontSize: 11, fontWeight: 600, padding: "6px 14px", cursor: "pointer",
              transition: "all 0.2s", letterSpacing: "0.04em",
            }}
          >
            Export Config
          </button>
          <button
            onClick={() => { setShowImport(!showImport); setShowExport(false); }}
            style={{
              background: showImport ? COLORS.accent : COLORS.surface,
              border: `1px solid ${showImport ? COLORS.accent : COLORS.border}`,
              borderRadius: 8,
              color: showImport ? COLORS.bg : COLORS.textSecondary,
              fontSize: 11, fontWeight: 600, padding: "6px 14px", cursor: "pointer",
              transition: "all 0.2s", letterSpacing: "0.04em",
            }}
          >
            Import Config
          </button>
          </div>
        </div>

        {/* Export Panel */}
        {showExport && (
          <div style={{
            marginBottom: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: 16,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Current Config — select all & copy
              </div>
              <button
                onClick={() => setShowExport(false)}
                style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6,
                  color: COLORS.textTertiary, fontSize: 11, fontWeight: 600, padding: "4px 12px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
            <pre
              onClick={e => {
                const range = document.createRange();
                range.selectNodeContents(e.target);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
              }}
              style={{
                background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`, borderRadius: 8,
                color: COLORS.textBody, fontFamily: "'Fragment Mono', monospace", fontSize: 11,
                padding: 12, margin: 0, cursor: "pointer", whiteSpace: "pre-wrap",
                userSelect: "all",
              }}
            >{configJson}</pre>
          </div>
        )}

        {/* Import Panel */}
        {showImport && (
          <div style={{
            marginBottom: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
              Paste JSON Config
            </div>
            <textarea
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder='{"srYield": 7, "stakingYield": 3.2, ...}'
              style={{
                width: "100%", minHeight: 80, background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`,
                borderRadius: 8, color: COLORS.textBody, fontFamily: "'Fragment Mono', monospace",
                fontSize: 11, padding: 12, resize: "vertical", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                onClick={handleImport}
                style={{
                  background: COLORS.accent, border: "none", borderRadius: 6,
                  color: COLORS.bg, fontSize: 11, fontWeight: 600, padding: "6px 16px",
                  cursor: "pointer",
                }}
              >
                Load Config
              </button>
              <button
                onClick={() => { setShowImport(false); setImportText(""); }}
                style={{
                  background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 6,
                  color: COLORS.textTertiary, fontSize: 11, fontWeight: 600, padding: "6px 16px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Configuration Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 28 }}>
          <Card title="Dawn Senior Vault" color={COLORS.accent}>
            <Input label="srRoyUSDC Yield" value={srYield} onChange={setSrYield} min={0} max={15} step={0.1} hint="Yield on USDC deposited into Dawn Senior" />
          </Card>

          <Card title="royETH — Carry Trade" color={COLORS.champagne}>
            <Input label="wstETH Staking Yield" value={stakingYield} onChange={setStakingYield} min={0} max={8} step={0.1} hint="Lido staking rewards embedded in wstETH" />
            <Input label="wstETH Supply APY" value={aaveSupplyYield} onChange={setAaveSupplyYield} min={0} max={0.5} step={0.01} hint="Interest on wstETH supplied as collateral" />
            <Input label="USDC Borrow Rate" value={usdcBorrowRate} onChange={setUsdcBorrowRate} min={1} max={20} step={0.1} />
            <LTVInput label="Effective LTV" value={aaveLTV} onChange={setAaveLTV}
              min={PARAM_BOUNDS.aaveLTV.min} max={Math.min(PARAM_BOUNDS.aaveLTV.max, aaveLiqThreshold - 1)} step={1}
              safeMax={calc.safeLTV.aave.max} liqThreshold={aaveLiqThreshold}
              hint={drawdownWindow > 0 && calc.windowHist ? `Survive ${calc.resolvedWin}d worst (${calc.effectiveEthDrop.toFixed(1)}%)` : `Survive ${ethDropPct}% ETH drop`} />
            <Input label="Liquidation Threshold" value={aaveLiqThreshold} onChange={setAaveLiqThreshold} min={80} max={99} step={0.5} hint="Aave v3 wstETH: 83%" />
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary }}>Drawdown Window</label>
                <span style={{ fontSize: 13, fontFamily: "'Fragment Mono', monospace", color: drawdownWindow > 0 ? COLORS.accent : COLORS.textTertiary }}>
                  {drawdownWindow === 0 ? "Manual" : `${drawdownWindow}d`}
                </span>
              </div>
              <input type="range" min={0} max={VALID_WINDOWS.length} step={1}
                value={drawdownWindow === 0 ? 0 : VALID_WINDOWS.indexOf(drawdownWindow) + 1}
                onChange={e => {
                  const idx = parseInt(e.target.value);
                  setDrawdownWindow(idx === 0 ? 0 : VALID_WINDOWS[idx - 1]);
                }}
                style={{ width: "100%" }}
              />
              <div style={{ fontSize: 10, color: COLORS.textTertiary, marginTop: 2 }}>
                0 = manual ETH drop; 1–{VALID_WINDOWS[VALID_WINDOWS.length - 1]}d = use historical worst for that reaction window
              </div>
            </div>
            {drawdownWindow > 0 && calc.windowHist ? (
              <div style={{
                background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`, borderRadius: 8,
                padding: 10, marginBottom: 12,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.accent, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  Using {calc.resolvedWin}d Historical Worst
                </div>
                <div style={{ fontSize: 12, fontFamily: "'Fragment Mono', monospace", color: COLORS.textBody }}>
                  {calc.effectiveEthDrop.toFixed(1)}% drop <span style={{ color: COLORS.textTertiary }}>({calc.windowHist.date}, {calc.windowHist.count} events)</span>
                </div>
              </div>
            ) : (
              <Input label="Stress Test ETH Drop" value={ethDropPct} onChange={setEthDropPct} min={10} max={70} step={1} hint="ETH/USD drop % for safe LTV calculation" />
            )}
          </Card>

          <Card title="eigenETH — Levered Loop" color={COLORS.champagne}>
            <LTVInput label="Effective LTV" value={morphoLTV} onChange={setMorphoLTV}
              min={PARAM_BOUNDS.morphoLTV.min} max={Math.min(PARAM_BOUNDS.morphoLTV.max, morphoLiqThreshold - 1)} step={1}
              safeMax={calc.safeLTV.morpho.max} liqThreshold={morphoLiqThreshold}
              hint={`Leverage: ${fmtN(1 / (1 - morphoLTV / 100), 2)}x`} />
            <Input label="ETH Borrow Rate Premium" value={ethBorrowPremium} onChange={setEthBorrowPremium} min={0} max={5} step={0.1} hint="Additional cost for borrowing ETH against royETH vs wstETH" />
            <div style={{
              background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`,
              borderRadius: 8, padding: 12, marginBottom: 12,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: COLORS.textTertiary, marginBottom: 6 }}>Effective ETH Borrow Rate</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, fontFamily: "'Fragment Mono', monospace" }}>
                <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{baseEthBorrowRate}%</span>
                <span style={{ fontSize: 11, color: COLORS.textTertiary }}>base</span>
                <span style={{ fontSize: 11, color: COLORS.textTertiary }}>+</span>
                <span style={{ fontSize: 11, color: COLORS.textBody }}>{ethBorrowPremium}%</span>
                <span style={{ fontSize: 11, color: COLORS.textTertiary }}>premium</span>
                <span style={{ fontSize: 11, color: COLORS.textTertiary }}>=</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: eigenEthBorrowRate > calc.royETHYield * 100 ? COLORS.negative : COLORS.textBody }}>{fmtN(eigenEthBorrowRate, 2)}%</span>
              </div>
              {eigenEthBorrowRate > calc.royETHYield * 100 && (
                <div style={{ marginTop: 8, padding: "6px 10px", background: COLORS.negativeBg, border: `1px solid ${COLORS.negativeBorder}`, borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: COLORS.negative, fontWeight: 700, lineHeight: 1.5 }}>
                    ⚠ Borrow rate ({fmtN(eigenEthBorrowRate, 2)}%) exceeds royETH yield ({fmt(calc.royETHYield)}) — each loop turn destroys value
                  </div>
                </div>
              )}
            </div>
            <div style={{
              background: calc.morphoSpread >= 0 ? COLORS.positiveBg : COLORS.negativeBg,
              border: `1px solid ${calc.morphoSpread >= 0 ? COLORS.positiveBorder : COLORS.negativeBorder}`,
              borderRadius: 8, padding: 12, marginBottom: 12,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: COLORS.textTertiary, marginBottom: 4 }}>Loop Yield Spread</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{
                  fontSize: 20, fontWeight: 800, fontFamily: "'Fragment Mono'",
                  color: calc.morphoSpread >= 0 ? COLORS.positive : COLORS.negative,
                }}>
                  {calc.morphoSpread >= 0 ? "+" : ""}{fmt(calc.morphoSpread)}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textTertiary }}>per loop turn</span>
              </div>
              <div style={{ fontSize: 10, color: calc.morphoSpread >= 0 ? COLORS.positive : COLORS.negative, marginTop: 4, lineHeight: 1.5 }}>
                {fmt(calc.royETHYield)} royETH yield − {fmtN(eigenEthBorrowRate, 2)}% borrow
                {calc.morphoSpread < 0 && " — looping is unprofitable at these rates"}
              </div>
            </div>
            <Input label="Liquidation Threshold" value={morphoLiqThreshold} onChange={setMorphoLiqThreshold} min={PARAM_BOUNDS.morphoLiqThreshold.min} max={PARAM_BOUNDS.morphoLiqThreshold.max} step={1} />
            <Toggle label="Model royETH Depeg Risk" value={modelDepeg} onChange={setModelDepeg} />
            {modelDepeg && (
              <Input label="royETH Depeg %" value={depegPct} onChange={setDepegPct} min={0} max={25} step={0.5} hint="Simulated royETH/ETH price deviation from NAV" />
            )}
          </Card>

          <Card title="Vanilla wstETH/ETH Loop" color={COLORS.accentAlt}>
            <Input label="ETH Borrow Rate (wstETH collateral)" value={baseEthBorrowRate} onChange={setBaseEthBorrowRate} min={0} max={10} step={0.1} hint="Known rate for borrowing ETH against wstETH" />
            <Toggle label="Mirror wstETH Supply APY" value={mirrorAaveSupply} onChange={setMirrorAaveSupply} />
            {mirrorAaveSupply
              ? <div style={{ fontSize: 11, color: COLORS.textTertiary, marginBottom: 12, fontFamily: "'Fragment Mono', monospace" }}>Supply APY: {aaveSupplyYield}% <span style={{ color: COLORS.textTertiary }}>(synced from carry trade)</span></div>
              : <Input label="wstETH Supply APY" value={vanillaSupplyYield} onChange={setVanillaSupplyYield} min={0} max={0.5} step={0.01} hint="Interest earned on wstETH collateral" />
            }
            <div style={{
              background: calc.vanillaSpread >= 0 ? COLORS.positiveBg : COLORS.negativeBg,
              border: `1px solid ${calc.vanillaSpread >= 0 ? COLORS.positiveBorder : COLORS.negativeBorder}`,
              borderRadius: 8, padding: 12, marginTop: 4, marginBottom: 12,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: COLORS.textTertiary, marginBottom: 4 }}>Loop Yield Spread</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{
                  fontSize: 20, fontWeight: 800, fontFamily: "'Fragment Mono'",
                  color: calc.vanillaSpread >= 0 ? COLORS.positive : COLORS.negative,
                }}>
                  {calc.vanillaSpread >= 0 ? "+" : ""}{fmt(calc.vanillaSpread)}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textTertiary }}>per loop turn</span>
              </div>
              <div style={{ fontSize: 10, color: calc.vanillaSpread >= 0 ? COLORS.positive : COLORS.negative, marginTop: 4, lineHeight: 1.5 }}>
                ({stakingYield}% staking + {effectiveVanillaSupply}% supply) − {baseEthBorrowRate}% borrow
                {calc.vanillaSpread < 0 && " — looping is unprofitable at these rates"}
              </div>
            </div>
            <div style={{ background: COLORS.surfaceAlt, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: 12, marginTop: 4 }}>
              <div style={{ fontSize: 10, color: COLORS.textTertiary, fontWeight: 600, marginBottom: 4 }}>COMPARISON BENCHMARK</div>
              <div style={{ fontSize: 10, color: COLORS.textTertiary, lineHeight: 1.6 }}>
                Standard wstETH/ETH loop at same {morphoLTV}% LTV ({fmtN(1 / (1 - morphoLTV / 100), 2)}x leverage).
                Borrow rate = {baseEthBorrowRate}%.
                Only earns staking yield ({stakingYield}%) + supply APY ({effectiveVanillaSupply}%), no royETH/Dawn spread.
              </div>
            </div>
          </Card>
        </div>

        {/* Historical Backtest Card */}
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
          padding: "20px 24px", marginBottom: 28,
          borderTop: `3px solid ${COLORS.textSecondary}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.textSecondary, margin: 0, fontFamily: "'Shippori Mincho B1', serif" }}>Historical Backtest</h3>
            <span style={{ fontSize: 10, color: COLORS.textTertiary, marginLeft: "auto" }}>
              ETH trough drawdowns {">"}20% since 2019 · Correlated carry trade + loop stress
            </span>
          </div>

          {/* Window Badge Row */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {calc.backtestByWindow.map(bw => {
              const isSelected = showBacktestDetail === bw.window;
              return (
                <div
                  key={bw.window}
                  onClick={() => setShowBacktestDetail(isSelected ? null : bw.window)}
                  title={`Worst: -${bw.worstDrop.toFixed(1)}% · ${bw.worstEvent || bw.worstDate}`}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    padding: "6px 12px", borderRadius: 8, cursor: "pointer", minWidth: 64,
                    background: bw.allPass
                      ? (isSelected ? COLORS.positiveBorder : COLORS.positiveBg)
                      : (isSelected ? COLORS.negativeBorder : COLORS.negativeBg),
                    border: `1px solid ${bw.allPass
                      ? (isSelected ? COLORS.positive : COLORS.positiveBorder)
                      : (isSelected ? COLORS.negative : COLORS.negativeBorder)}`,
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: bw.allPass ? COLORS.positive : COLORS.negative }}>
                      {bw.allPass ? "✓" : "✗"}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, fontFamily: "'Fragment Mono', monospace", color: COLORS.textBody }}>
                      {bw.window}d
                    </span>
                    <span style={{ fontSize: 9, color: COLORS.textTertiary }}>
                      {bw.passCount}/{bw.totalEvents}
                    </span>
                  </div>
                  {!bw.allPass && (
                    <div style={{ fontSize: 9, color: COLORS.accent, fontFamily: "'Fragment Mono', monospace", marginTop: 2 }}>
                      Repay: {Math.max(...bw.events.map(e => e.repayPct)).toFixed(1)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary Line */}
          <div style={{ marginBottom: 12 }}>
            {calc.backtestByWindow.every(bw => bw.allPass) ? (
              <div style={{ fontSize: 12, color: COLORS.positive, fontWeight: 600 }}>
                ✓ Survives all historical events across all time windows
              </div>
            ) : calc.backtestByWindow.every(bw => !bw.allPass) ? (
              <div style={{ fontSize: 12, color: COLORS.negative, fontWeight: 600 }}>
                ✗ Fails at all time windows — config too aggressive for any historical drawdown
              </div>
            ) : (
              <div style={{ fontSize: 12, color: COLORS.textBody }}>
                <span style={{ color: COLORS.positive, fontWeight: 600 }}>Survives</span> all historical events through{" "}
                <span style={{ fontWeight: 700, fontFamily: "'Fragment Mono', monospace", color: COLORS.positive }}>
                  {calc.maxSurvivableWindow}d
                </span> windows
                <span style={{ color: COLORS.textTertiary }}> · </span>
                <span style={{ color: COLORS.negative, fontWeight: 600 }}>Fails at: </span>
                {calc.backtestByWindow.filter(bw => !bw.allPass).map(bw => (
                  <span key={bw.window} style={{ fontFamily: "'Fragment Mono', monospace", color: COLORS.negative }}>
                    {bw.window}d ({bw.failCount}) {" "}
                  </span>
                ))}
              </div>
            )}
            {!calc.morphoSurvives && (
              <div style={{ fontSize: 11, color: COLORS.negative, marginTop: 6 }}>
                ⚠ Depeg ({(modelDepeg ? depegPct : DEFAULT_DEPEG_PCT)}%) alone causes liquidation — all events fail regardless of ETH price
              </div>
            )}
            {calc.morphoSurvives && (
              <div style={{ fontSize: 10, color: COLORS.textTertiary, marginTop: 4 }}>
                Depeg ({(modelDepeg ? depegPct : DEFAULT_DEPEG_PCT)}%): ✓ safe
              </div>
            )}
          </div>

          {/* Expandable Detail for Selected Window */}
          {showBacktestDetail != null && (() => {
            const bw = calc.backtestByWindow.find(b => b.window === showBacktestDetail);
            if (!bw) return null;
            return (
              <div style={{
                background: COLORS.surfaceAlt, border: `1px solid ${COLORS.borderLight}`, borderRadius: 8,
                padding: 16, marginTop: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textBody }}>
                    {bw.window}-Day Window · {bw.totalEvents} events
                  </div>
                  <span style={{ fontSize: 10, color: COLORS.textTertiary, cursor: "pointer" }} onClick={() => setShowBacktestDetail(null)}>Close ✕</span>
                </div>
                <div style={{ fontSize: 10, color: COLORS.textTertiary, marginBottom: 12 }}>
                  Worst: <span style={{ color: COLORS.negative, fontFamily: "'Fragment Mono', monospace" }}>-{bw.worstDrop.toFixed(2)}%</span>
                  {" · "}{bw.worstEvent || bw.worstDate}
                  {bw.events.some(e => e.repayPct > 0) && (
                    <span>
                      {" · "}Max callback from srRoyUSDC: <span style={{ color: COLORS.accent, fontFamily: "'Fragment Mono', monospace" }}>
                        {Math.max(...bw.events.map(e => e.repayPct)).toFixed(1)}%
                      </span> of borrowed USDC
                    </span>
                  )}
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr>
                        {["Date", "Event", "ETH Drop", "Headroom"].map(h => (
                          <th key={h} style={{ padding: "4px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                        ))}
                        <th style={{ padding: "4px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${COLORS.border}` }} title="% of borrowed USDC to call back from srRoyUSDC and repay to lender to avoid liquidation">
                          Repay % <span style={{ fontSize: 8, color: COLORS.border, fontWeight: 400 }}>of borrowed</span>
                        </th>
                        <th style={{ padding: "4px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${COLORS.border}` }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bw.events.map((ev, i) => (
                        <tr key={i} style={{ background: !ev.survives ? COLORS.negativeBg : "transparent" }}>
                          <td style={{ padding: "4px 8px", fontFamily: "'Fragment Mono'", color: COLORS.textSecondary, borderBottom: `1px solid ${COLORS.borderLight}`, fontSize: 10, whiteSpace: "nowrap" }}>{ev.date}</td>
                          <td style={{ padding: "4px 8px", color: COLORS.textTertiary, borderBottom: `1px solid ${COLORS.borderLight}`, fontSize: 10, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={ev.event}>{ev.event}</td>
                          <td style={{ padding: "4px 8px", fontFamily: "'Fragment Mono'", color: COLORS.textBody, borderBottom: `1px solid ${COLORS.borderLight}` }}>-{ev.drawdown.toFixed(2)}%</td>
                          <td style={{ padding: "4px 8px", fontFamily: "'Fragment Mono'", color: ev.headroom >= 0 ? COLORS.positive : COLORS.negative, borderBottom: `1px solid ${COLORS.borderLight}` }}>
                            {ev.headroom >= 0 ? "+" : ""}{ev.headroom.toFixed(1)}%
                          </td>
                          <td style={{ padding: "4px 8px", fontFamily: "'Fragment Mono'", borderBottom: `1px solid ${COLORS.borderLight}`, whiteSpace: "nowrap" }}
                            title={ev.repayPct > 0 ? `Withdraw ${ev.repayPct.toFixed(1)}% of borrowed USDC from srRoyUSDC → repay lender to survive` : "No repayment needed — position survives as-is"}>
                            {ev.repayPct > 0
                              ? <span style={{ color: COLORS.accent, fontWeight: 600, fontSize: 10 }}>{ev.repayPct.toFixed(1)}%</span>
                              : <span style={{ color: COLORS.textTertiary, fontSize: 10 }}>—</span>
                            }
                          </td>
                          <td style={{ padding: "4px 8px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                            {ev.survives
                              ? <span style={{ color: COLORS.positive, fontSize: 10 }}>✓ Survives</span>
                              : <span style={{ color: COLORS.negative, fontWeight: 700, fontSize: 10 }}>✗ Liquidated</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Constraint Dashboard — Collapsible */}
        <div style={{
          background: COLORS.surface, border: `1px solid ${calc.passingCount === calc.totalCount ? COLORS.positiveBorder : COLORS.negativeBorder}`,
          borderRadius: 12, marginBottom: 28, overflow: "hidden",
        }}>
          <div
            onClick={() => setShowConstraints(!showConstraints)}
            style={{
              padding: "14px 20px", cursor: "pointer", userSelect: "none",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{
                fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
                color: calc.passingCount === calc.totalCount ? COLORS.positive : COLORS.negative,
              }}>
                {calc.passingCount === calc.totalCount ? "✓" : "⚠"} Constraints: {calc.passingCount}/{calc.totalCount} passing
              </span>
              {calc.passingCount < calc.totalCount && (
                <span style={{ fontSize: 10, color: COLORS.negative }}>
                  — {Object.entries(calc.constraints).filter(([,c]) => !c.pass).map(([,c]) => c.label).join(", ")}
                </span>
              )}
            </div>
            <span style={{ fontSize: 12, color: COLORS.textTertiary, transform: showConstraints ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
          </div>

          {showConstraints && (
            <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${COLORS.border}` }}>
              {/* Constraint Table */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginTop: 16 }}>
                <thead>
                  <tr>
                    {["Status", "Constraint", "Headroom"].map(h => (
                      <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(calc.constraints).map(([key, c]) => (
                    <tr key={key}>
                      <td style={{ padding: "6px 8px", borderBottom: `1px solid ${COLORS.borderLight}`, width: 60 }}>
                        <span style={{
                          fontSize: 9, fontWeight: 800, letterSpacing: "0.1em",
                          padding: "2px 6px", borderRadius: 3,
                          background: c.pass ? COLORS.positiveBg : COLORS.negativeBg,
                          border: `1px solid ${c.pass ? COLORS.positiveBorder : COLORS.negativeBorder}`,
                          color: c.pass ? COLORS.positive : COLORS.negative,
                        }}>{c.pass ? "PASS" : "FAIL"}</span>
                      </td>
                      <td style={{ padding: "6px 8px", borderBottom: `1px solid ${COLORS.borderLight}`, color: COLORS.textBody }}>{c.label}</td>
                      <td style={{ padding: "6px 8px", borderBottom: `1px solid ${COLORS.borderLight}`, fontFamily: "'Fragment Mono', monospace", color: c.headroom >= 0 ? COLORS.positive : COLORS.negative }}>
                        {c.headroom != null ? `${c.headroom >= 0 ? "+" : ""}${fmt(c.headroom)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Critical Thresholds */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.textSecondary, marginBottom: 10, fontFamily: "'Shippori Mincho B1', serif" }}>Critical Thresholds</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
                  {Object.entries(calc.thresholds).map(([key, t]) => (
                    <div key={key} style={{ background: COLORS.surfaceAlt, borderRadius: 6, padding: "8px 12px", border: `1px solid ${COLORS.borderLight}` }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>{t.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Fragment Mono'", color: COLORS.textBody, marginTop: 2 }}>
                        {t.value === Infinity ? "∞" : fmtN(t.value) + "%"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Safe LTV Summary */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.textSecondary, marginBottom: 10, fontFamily: "'Shippori Mincho B1', serif" }}>Safe LTV Ranges</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { label: "Carry Trade", color: COLORS.textBody, data: calc.safeLTV.aave },
                    { label: "Levered Loop", color: COLORS.textBody, data: calc.safeLTV.morpho },
                  ].map(({ label, color, data }) => (
                    <div key={label} style={{ background: COLORS.surfaceAlt, borderRadius: 6, padding: "10px 14px", border: `1px solid ${COLORS.borderLight}` }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'Fragment Mono'", color: COLORS.textBody }}>{data.current.toFixed(1)}%</span>
                        <span style={{ fontSize: 10, color: COLORS.textTertiary }}>/ max</span>
                        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'Fragment Mono'", color: data.headroom >= 0 ? COLORS.positive : COLORS.negative }}>{data.max.toFixed(1)}%</span>
                        <span style={{ fontSize: 10, fontFamily: "'Fragment Mono'", color: data.headroom >= 0 ? COLORS.positive : COLORS.negative }}>
                          ({data.headroom >= 0 ? "+" : ""}{data.headroom.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Yield Results */}
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
          padding: "24px", marginBottom: 28
        }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary, margin: "0 0 20px", fontFamily: "'Shippori Mincho B1', serif" }}>
            Yield Waterfall
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
            <div style={{ background: COLORS.surfaceAlt, borderRadius: 10, padding: 16, border: `1px solid ${COLORS.borderLight}` }}>
              <Metric label="Dawn Senior · srRoyUSDC" value={fmt(calc.dawnYield)} color={COLORS.accent} sub="Set by user" />
            </div>
            <div style={{ background: COLORS.surfaceAlt, borderRadius: 10, padding: 16, border: `1px solid ${COLORS.borderLight}` }}>
              <Metric
                label="royETH"
                value={fmt(calc.royETHYield)}
                color={COLORS.textBody}
                sub={`${fmt(calc.roycoStakingComponent)} staking + ${fmt(calc.roycoSupplyComponent)} supply + ${fmt(calc.roycoSpreadComponent)} spread`}
              />
            </div>
            <div style={{ background: COLORS.surfaceAlt, borderRadius: 10, padding: 16, border: `1px solid ${COLORS.borderLight}` }}>
              <Metric
                label="eigenETH · Looped"
                value={fmt(calc.eigenvaultYield)}
                color={calc.eigenvaultYield >= 0 ? COLORS.ink : COLORS.negative}
                sub={`${fmtN(calc.leverage, 2)}x leverage | ${fmt(calc.eigenRoyETHGross)} gross − ${fmt(calc.eigenBorrowCost)} borrow`}
              />
            </div>
            <div style={{ background: COLORS.surfaceAlt, borderRadius: 10, padding: 16, border: `1px solid ${COLORS.borderLight}` }}>
              <Metric
                label="Vanilla wstETH/ETH Loop"
                value={fmt(calc.vanillaYield)}
                color={COLORS.accentAlt}
                sub={`${fmtN(calc.leverage, 2)}x leverage | ${fmt(calc.vanillaGross)} gross − ${fmt(calc.vanillaBorrowCost)} borrow`}
              />
            </div>
          </div>

          {/* eigenETH vs Vanilla Comparison Banner */}
          <div style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
            borderRadius: 10, padding: "16px 20px", marginBottom: 24,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: COLORS.textSecondary, marginBottom: 16, fontFamily: "'Shippori Mincho B1', serif" }}>
              eigenETH Yield Comparison
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              {/* vs Vanilla Loop (same leverage) */}
              <div style={{
                background: calc.yieldAdvantage >= 0 ? COLORS.positiveBg : COLORS.negativeBg,
                border: `1px solid ${calc.yieldAdvantage >= 0 ? COLORS.positiveBorder : COLORS.negativeBorder}`,
                borderRadius: 8, padding: 16,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>vs Vanilla ({fmtN(calc.leverage, 1)}x)</div>
                  <div style={{
                    fontSize: 22, fontWeight: 800, fontFamily: "'Fragment Mono'",
                    color: calc.yieldAdvantage >= 0 ? COLORS.positive : COLORS.negative,
                  }}>
                    {calc.yieldAdvantage >= 0 ? "+" : ""}{fmt(calc.yieldAdvantage)}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.6 }}>
                  {fmt(calc.eigenvaultYield)} eigenETH vs {fmt(calc.vanillaYield)} vanilla at {fmtN(calc.leverage, 2)}x leverage.
                  {calc.yieldAdvantage >= 0
                    ? " The royETH/Dawn spread compensates for higher royETH borrow cost."
                    : " The royETH/Dawn spread doesn't overcome the royETH borrow premium."
                  }
                </div>
              </div>
              {/* vs 1x wstETH */}
              <div style={{
                background: calc.yieldVs1x >= 0 ? COLORS.positiveBg : COLORS.negativeBg,
                border: `1px solid ${calc.yieldVs1x >= 0 ? COLORS.positiveBorder : COLORS.negativeBorder}`,
                borderRadius: 8, padding: 16,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.08em" }}>vs 1x wstETH</div>
                  <div style={{
                    fontSize: 22, fontWeight: 800, fontFamily: "'Fragment Mono'",
                    color: calc.yieldVs1x >= 0 ? COLORS.positive : COLORS.negative,
                  }}>
                    {calc.yieldVs1x >= 0 ? "+" : ""}{fmt(calc.yieldVs1x)}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.6 }}>
                  {fmt(calc.eigenvaultYield)} eigenETH vs {fmt(calc.oneXwstETH)} bare wstETH staking.
                  {calc.yieldVs1x >= 0
                    ? " The full structure generates additional yield over simple staking."
                    : " At current rates, complexity destroys value vs just holding wstETH."
                  }
                </div>
              </div>
              {/* vs Vanilla Loop 90% LTV */}
              <div style={{
                background: calc.yieldVs90 >= 0 ? COLORS.positiveBg : COLORS.negativeBg,
                border: `1px solid ${calc.yieldVs90 >= 0 ? COLORS.positiveBorder : COLORS.negativeBorder}`,
                borderRadius: 8, padding: 16,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>vs 10x Vanilla (90% LTV)</div>
                  <div style={{
                    fontSize: 22, fontWeight: 800, fontFamily: "'Fragment Mono'",
                    color: calc.yieldVs90 >= 0 ? COLORS.positive : COLORS.negative,
                  }}>
                    {calc.yieldVs90 >= 0 ? "+" : ""}{fmt(calc.yieldVs90)}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.6 }}>
                  {fmt(calc.eigenvaultYield)} eigenETH at {fmtN(calc.leverage, 1)}x vs {fmt(calc.vanillaYield90)} vanilla at {fmtN(calc.vanillaLeverage90, 0)}x leverage.
                  {calc.yieldVs90 >= 0
                    ? " eigenETH outperforms even an aggressive 10x vanilla loop."
                    : " 90% LTV carries high risk of liquidation in the event of a wstETH depeg."
                  }
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {/* Yield Waterfall Chart */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                Effective Yield by Layer
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={yieldWaterfallData} barSize={48}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: COLORS.textSecondary }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: COLORS.textSecondary, fontFamily: "Fragment Mono" }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} tickFormatter={v => v + "%"} />
                  <ReferenceLine y={0} stroke={COLORS.border} />
                  <Tooltip
                    contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [v.toFixed(2) + "%", "Yield"]}
                  />
                  <Bar dataKey="yield" radius={[6, 6, 0, 0]}>
                    {yieldWaterfallData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* royETH Breakdown */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                royETH Yield Breakdown
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={roycoBreakdownData} barSize={48}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: COLORS.textSecondary }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: COLORS.textSecondary, fontFamily: "Fragment Mono" }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} tickFormatter={v => v + "%"} />
                  <ReferenceLine y={0} stroke={COLORS.border} />
                  <Tooltip
                    contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [v.toFixed(2) + "%", "Yield"]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {roycoBreakdownData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Liquidation Analysis */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
          {/* Aave Liquidation */}
          <div style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
            padding: "24px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textBody, margin: 0, fontFamily: "'Shippori Mincho B1', serif" }}>
                Carry Trade Liquidation · ETH/USD Drawdown
              </h2>
              <StatusBadge level={riskLevel(calc.aaveHealthFactor)} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.borderLight}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>Health Factor</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Fragment Mono'", color: COLORS.accent, marginTop: 4 }}>
                  {fmtN(calc.aaveHealthFactor, 2)}
                </div>
              </div>
              <div style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.borderLight}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>Max ETH Drop</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Fragment Mono'", color: calc.aaveMaxDrop > 0.25 ? COLORS.positive : COLORS.negative, marginTop: 4 }}>
                  -{(calc.aaveMaxDrop * 100).toFixed(1)}%
                </div>
              </div>
              <div style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.borderLight}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>Current LTV</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Fragment Mono'", color: COLORS.ink, marginTop: 4 }}>
                  {aaveLTV}%
                </div>
              </div>
            </div>

            {/* Drawdown Chart */}
            <div style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={calc.aaveDrawdowns.filter(d => d.drop <= 60)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: COLORS.textSecondary }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} />
                  <YAxis yAxisId="ltv" tick={{ fontSize: 9, fill: COLORS.textSecondary, fontFamily: "Fragment Mono" }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} tickFormatter={v => (v * 100).toFixed(0) + "%"} domain={[0, 1.1]} />
                  <YAxis yAxisId="hf" orientation="right" tick={{ fontSize: 9, fill: COLORS.textSecondary, fontFamily: "Fragment Mono" }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} domain={[0, 'auto']} />
                  <Tooltip
                    contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 11 }}
                    formatter={(v, name) => [name === "ltv" ? (v * 100).toFixed(1) + "%" : fmtN(v, 2), name === "ltv" ? "LTV" : "Health Factor"]}
                  />
                  <ReferenceLine yAxisId="ltv" y={aaveLiqThreshold / 100} stroke={COLORS.negative + "80"} strokeDasharray="4 4" label={{ value: `Liq ${aaveLiqThreshold}%`, fill: COLORS.negative, fontSize: 9, position: "right" }} />
                  <ReferenceLine yAxisId="hf" y={1} stroke={COLORS.accent + "80"} strokeDasharray="4 4" />
                  <Area yAxisId="ltv" dataKey="ltv" fill={COLORS.accent + "10"} stroke={COLORS.accent} strokeWidth={2} dot={false} />
                  <Line yAxisId="hf" dataKey="hf" stroke={COLORS.accent} strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Drawdown Table */}
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: COLORS.surface }}>
                    {["ETH Drop", "Eff. LTV", "Health Factor", "Status"].map(h => (
                      <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calc.aaveDrawdowns.filter(d => d.drop <= 60).map((d, i) => (
                    <tr key={i} style={{ background: d.liquidated ? COLORS.negativeBg : "transparent" }}>
                      <td style={{ padding: "5px 8px", fontFamily: "'Fragment Mono'", color: COLORS.textBody, borderBottom: `1px solid ${COLORS.borderLight}` }}>{d.label}</td>
                      <td style={{ padding: "5px 8px", fontFamily: "'Fragment Mono'", color: d.ltv >= aaveLiqThreshold / 100 ? COLORS.negative : d.ltv >= aaveLiqThreshold / 100 * 0.9 ? COLORS.accent : COLORS.textBody, borderBottom: `1px solid ${COLORS.borderLight}` }}>{(d.ltv * 100).toFixed(1)}%</td>
                      <td style={{ padding: "5px 8px", fontFamily: "'Fragment Mono'", color: d.hf < 1 ? COLORS.negative : d.hf < 1.15 ? COLORS.caution : COLORS.accent, borderBottom: `1px solid ${COLORS.borderLight}` }}>{fmtN(d.hf, 3)}</td>
                      <td style={{ padding: "5px 8px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                        {d.liquidated ? <span style={{ color: COLORS.negative, fontWeight: 700, fontSize: 10 }}>⚠ LIQUIDATED</span> : <span style={{ color: COLORS.positive, fontSize: 10 }}>✓</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Morpho Liquidation */}
          <div style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
            padding: "24px",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textBody, margin: 0, fontFamily: "'Shippori Mincho B1', serif" }}>
                Loop Liquidation · royETH Depeg
              </h2>
              <StatusBadge level={modelDepeg ? riskLevel(calc.morphoHFAfterDepeg) : "safe"} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.borderLight}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>Health Factor</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Fragment Mono'", color: COLORS.accent, marginTop: 4 }}>
                  {fmtN(modelDepeg ? calc.morphoHFAfterDepeg : calc.morphoHF, 2)}
                  {modelDepeg && <span style={{ fontSize: 10, color: COLORS.textTertiary }}> @ -{depegPct}%</span>}
                </div>
              </div>
              <div style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.borderLight}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>Max Depeg</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Fragment Mono'", color: calc.morphoMaxDepeg > 0.1 ? COLORS.positive : COLORS.negative, marginTop: 4 }}>
                  -{(calc.morphoMaxDepeg * 100).toFixed(1)}%
                </div>
              </div>
              <div style={{ background: COLORS.surfaceAlt, borderRadius: 8, padding: 12, border: `1px solid ${COLORS.borderLight}` }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em" }}>Leverage</div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Fragment Mono'", color: COLORS.ink, marginTop: 4 }}>
                  {fmtN(calc.leverage, 2)}x
                </div>
              </div>
            </div>

            {!modelDepeg && (
              <div style={{
                background: COLORS.positiveBg, border: `1px solid ${COLORS.positiveBorder}`, borderRadius: 8,
                padding: 16, marginBottom: 16
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.positive, marginBottom: 6 }}>NAV Mode: No Market Liquidation Risk</div>
                <div style={{ fontSize: 11, color: COLORS.positive, lineHeight: 1.5 }}>
                  When royETH trades at NAV, both collateral (royETH) and debt (ETH) are ETH-denominated.
                  The collateral only appreciates relative to debt via yield accrual, so there is no market-driven
                  liquidation risk. Risk only exists if the underlying vault loses value (smart contract risk, bad debt).
                </div>
                <div style={{ fontSize: 11, color: COLORS.positive, marginTop: 8, fontWeight: 600 }}>
                  Toggle "Model royETH Depeg Risk" above to analyze depeg scenarios.
                </div>
              </div>
            )}

            {modelDepeg && (
              <>
                {/* Depeg Chart */}
                <div style={{ marginBottom: 16 }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <ComposedChart data={calc.morphoDepegScenarios.filter(d => d.depeg <= 20)}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: COLORS.textSecondary }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} />
                      <YAxis yAxisId="ltv" tick={{ fontSize: 9, fill: COLORS.textSecondary, fontFamily: "Fragment Mono" }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} tickFormatter={v => (v * 100).toFixed(0) + "%"} domain={[0, 1.1]} />
                      <YAxis yAxisId="hf" orientation="right" tick={{ fontSize: 9, fill: COLORS.textSecondary, fontFamily: "Fragment Mono" }} axisLine={{ stroke: COLORS.borderLight }} tickLine={false} domain={[0, 'auto']} />
                      <Tooltip
                        contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 11 }}
                        formatter={(v, name) => [name === "ltv" ? (v * 100).toFixed(1) + "%" : fmtN(v, 2), name === "ltv" ? "LTV" : "Health Factor"]}
                      />
                      <ReferenceLine yAxisId="ltv" y={morphoLiqThreshold / 100} stroke={COLORS.negative + "80"} strokeDasharray="4 4" label={{ value: `Liq ${morphoLiqThreshold}%`, fill: COLORS.negative, fontSize: 9, position: "right" }} />
                      <ReferenceLine yAxisId="hf" y={1} stroke={COLORS.accent + "80"} strokeDasharray="4 4" />
                      <Area yAxisId="ltv" dataKey="ltv" fill={COLORS.champagne + "10"} stroke={COLORS.champagne} strokeWidth={2} dot={false} />
                      <Line yAxisId="hf" dataKey="hf" stroke={COLORS.accent} strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Depeg Table */}
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ position: "sticky", top: 0, background: COLORS.surface }}>
                        {["royETH Depeg", "Eff. LTV", "Health Factor", "Status"].map(h => (
                          <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: COLORS.textTertiary, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {calc.morphoDepegScenarios.filter(d => d.depeg <= 20).map((d, i) => (
                        <tr key={i} style={{ background: d.liquidated ? COLORS.negativeBg : d.depeg === depegPct ? COLORS.surfaceAlt : "transparent" }}>
                          <td style={{ padding: "5px 8px", fontFamily: "'Fragment Mono'", color: COLORS.textBody, borderBottom: `1px solid ${COLORS.borderLight}` }}>
                            {d.label} {d.depeg === depegPct && <span style={{ color: COLORS.textBody, fontSize: 9 }}>◄</span>}
                          </td>
                          <td style={{ padding: "5px 8px", fontFamily: "'Fragment Mono'", color: d.ltv >= morphoLiqThreshold / 100 ? COLORS.negative : d.ltv >= morphoLiqThreshold / 100 * 0.9 ? COLORS.accent : COLORS.textBody, borderBottom: `1px solid ${COLORS.borderLight}` }}>{(d.ltv * 100).toFixed(1)}%</td>
                          <td style={{ padding: "5px 8px", fontFamily: "'Fragment Mono'", color: d.hf < 1 ? COLORS.negative : d.hf < 1.15 ? COLORS.caution : COLORS.accent, borderBottom: `1px solid ${COLORS.borderLight}` }}>{fmtN(d.hf, 3)}</td>
                          <td style={{ padding: "5px 8px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                            {d.liquidated ? <span style={{ color: COLORS.negative, fontWeight: 700, fontSize: 10 }}>⚠ LIQUIDATED</span> : <span style={{ color: COLORS.positive, fontSize: 10 }}>✓</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Formulas Reference */}
        <div style={{
          background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
          padding: "24px", marginBottom: 28
        }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.textSecondary, margin: "0 0 16px", fontFamily: "'Shippori Mincho B1', serif" }}>
            Calculation Reference
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textBody, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>royETH Yield</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                royETH_yield = wstETH_staking<br />
                &nbsp;&nbsp;+ supply_apy<br />
                &nbsp;&nbsp;+ eff_LTV × (srRoyUSDC_yield − usdc_borrow_rate)<br />
                <br />
                = {fmt(calc.roycoStakingComponent)} + {fmt(calc.roycoSupplyComponent)} + {aaveLTV}% × ({srYield}% − {usdcBorrowRate}%)<br />
                = <span style={{ color: COLORS.textBody, fontWeight: 700 }}>{fmt(calc.royETHYield)}</span>
              </code>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textBody, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>eigenETH Looped Yield</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                leverage = 1 / (1 − loop_LTV) = {fmtN(calc.leverage, 2)}x<br />
                <br />
                eigenETH_yield = leverage × royETH_yield<br />
                &nbsp;&nbsp;− (leverage − 1) × (base_rate + premium)<br />
                <br />
                = {fmtN(calc.leverage, 2)} × {fmt(calc.royETHYield)} − {fmtN(calc.leverage - 1, 2)} × ({baseEthBorrowRate}% + {ethBorrowPremium}%)<br />
                = <span style={{ color: COLORS.ink, fontWeight: 700 }}>{fmt(calc.eigenvaultYield)}</span>
              </code>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textBody, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Carry Trade Liquidation</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                max_eth_drop = 1 − (eff_LTV / liq_threshold)<br />
                = 1 − ({aaveLTV}% / {aaveLiqThreshold}%)<br />
                = <span style={{ color: calc.aaveMaxDrop > 0.2 ? COLORS.positive : COLORS.negative, fontWeight: 700 }}>-{(calc.aaveMaxDrop * 100).toFixed(1)}%</span>
              </code>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textBody, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Loop Liquidation</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                max_depeg = 1 − (eff_LTV / liq_threshold)<br />
                = 1 − ({morphoLTV}% / {morphoLiqThreshold}%)<br />
                = <span style={{ color: calc.morphoMaxDepeg > 0.1 ? COLORS.positive : COLORS.negative, fontWeight: 700 }}>-{(calc.morphoMaxDepeg * 100).toFixed(1)}%</span>
                <br /><br />
                <span style={{ color: COLORS.textTertiary }}>Note: Only applicable if royETH deviates from NAV.</span><br />
                <span style={{ color: COLORS.textTertiary }}>At NAV, ETH-denominated collateral & debt = no market liq risk.</span>
              </code>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accentAlt, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Vanilla wstETH/ETH Loop Yield</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                vanilla_yield = leverage × (staking_yield + supply_apy)<br />
                &nbsp;&nbsp;− (leverage − 1) × base_eth_borrow_rate<br />
                <br />
                = {fmtN(calc.leverage, 2)} × ({stakingYield}% + {effectiveVanillaSupply}%) − {fmtN(calc.leverage - 1, 2)} × {baseEthBorrowRate}%<br />
                = <span style={{ color: COLORS.accentAlt, fontWeight: 700 }}>{fmt(calc.vanillaYield)}</span>
              </code>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textTertiary, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Vanilla 90% LTV (10x Leverage)</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                vanilla_90 = 10 × (staking + supply) − 9 × borrow<br />
                = 10 × ({stakingYield}% + {effectiveVanillaSupply}%) − 9 × {baseEthBorrowRate}%<br />
                = <span style={{ color: COLORS.textTertiary, fontWeight: 700 }}>{fmt(calc.vanillaYield90)}</span><br />
                <br />
                <span style={{ color: COLORS.textTertiary }}>vs eigenETH: {calc.yieldVs90 >= 0 ? "+" : ""}{fmt(calc.yieldVs90)} alpha</span>
              </code>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: calc.yieldAdvantage >= 0 ? COLORS.positive : COLORS.negative, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>eigenETH Alpha vs Vanilla ({fmtN(calc.leverage, 1)}x)</div>
              <code style={{ fontSize: 11, color: COLORS.textBody, lineHeight: 1.8, display: "block", background: COLORS.surfaceAlt, padding: 12, borderRadius: 8, fontFamily: "'Fragment Mono'" }}>
                alpha = eigenETH_yield − vanilla_yield<br />
                = {fmt(calc.eigenvaultYield)} − {fmt(calc.vanillaYield)}<br />
                = <span style={{ color: calc.yieldAdvantage >= 0 ? COLORS.positive : COLORS.negative, fontWeight: 700 }}>{calc.yieldAdvantage >= 0 ? "+" : ""}{fmt(calc.yieldAdvantage)}</span><br />
                <br />
                <span style={{ color: COLORS.textTertiary }}>Alpha source: royETH yield ({fmt(calc.royETHYield)}) vs</span><br />
                <span style={{ color: COLORS.textTertiary }}>raw staking ({fmt(calc.roycoStakingComponent)}) = +{fmt(calc.royETHYield - calc.roycoStakingComponent)} from royETH/Dawn</span><br />
                <span style={{ color: COLORS.textTertiary }}>Cost: +{ethBorrowPremium}% borrow premium for royETH vs wstETH</span>
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
